/** @format */

import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	InputAdornment,
	Stack,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import { selectCurrentUser } from "../../../redux/selectors/userSelector";
import { sendCircleInvite } from "../../../redux/actions/circleInvitationAction";
import { buttonColors, colors } from "../../../utility/types/constant";
import Groups2RoundedIcon from "@mui/icons-material/Groups2Rounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { selectAllCircles } from "../../../redux/selectors/circleSelector";
import { isCycleInProgress } from "../../../utility/utils/mid-cycle-join-utils";
import { selectCurrentCycleOfCircle } from "../../../redux/selectors/contributionSelector";

interface SendCircleInviteModalProps {
	open: boolean;
	handleClose: () => void;
	circleId: string;
	circleName?: string;
}

type FormValues = {
	inviteeFirstName: string;
	inviteeLastName: string;
	inviteeEmail: string;
	inviteePhone: string;
};

type FormErrors = Partial<Record<keyof FormValues, string>>;

const initialValues: FormValues = {
	inviteeFirstName: "",
	inviteeLastName: "",
	inviteeEmail: "",
	inviteePhone: "",
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// relaxed phone validation for UX, backend can still enforce E.164 if desired
const phoneRegex = /^[0-9+\-()\s]{7,20}$/;

const SendCircleInviteModal = (props: SendCircleInviteModalProps) => {
	const dispatch = useDispatch<AppDispatch>();
	const user = useSelector(selectCurrentUser);
	const circleData = useSelector(selectAllCircles);
	// const currentCycle = useSelector(selectCurrentCycleOfCircle)
	// console.log("current cycle ===>", currentCycle)

	const { open, handleClose, circleId, circleName } = props;

	const theme = useTheme();

	const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

	// const [showNotificationModal, setShowNotificationModal] = useState(false);

	// const [notificationMessage, setNotificationMessage] = useState("");

	const [formValues, setFormValues] = useState<FormValues>(initialValues);

	const [formErrors, setFormErrors] = useState<FormErrors>({});

	const [isSubmitting, setIsSubmitting] = useState(false);

	const [submitError, setSubmitError] = useState("");

	const [submitSuccess, setSubmitSuccess] = useState("");

	const currentCircle = useMemo(() => {
		if (!Array.isArray(circleData)) return null;
		return circleData.find((circle: any) => circle._id === circleId) || null;
	}, [circleData, circleId]);

	const currentCycle = useSelector((state: any) =>
		selectCurrentCycleOfCircle(state, circleId),
	);

	// Invites are blocked only while a cycle has started and not yet ended.
	const inProgressCycle = useMemo(() => {
		if (isCycleInProgress(currentCircle?.activeCycleInfo)) {
			return currentCircle.activeCycleInfo;
		}
		if (isCycleInProgress(currentCycle)) {
			return currentCycle;
		}
		return null;
	}, [currentCircle, currentCycle]);

	const disableSendInvite = Boolean(inProgressCycle);

	const inviteWindowMessage =
		inProgressCycle ?
			`Invites are disabled while a contribution cycle is in progress. You can send invites after the cycle ends on ${new Date(
				inProgressCycle.endDate,
			).toLocaleDateString("en-US", {
				month: "short",
				day: "numeric",
				year: "numeric",
				timeZone: "UTC",
			})}.`
		:	"";

	const normalizedEmail = useMemo(
		() => formValues.inviteeEmail.trim().toLowerCase(),
		[formValues.inviteeEmail],
	);

	const resetForm = () => {
		setFormValues(initialValues);
		setFormErrors({});
		setSubmitError("");
		setSubmitSuccess("");
		setIsSubmitting(false);
	};

	const onCloseModal = () => {
		if (isSubmitting) {
			return;
		}
		resetForm();
		handleClose();
	};

	const validateForm = (): boolean => {
		const errors: FormErrors = {};

		if (!formValues.inviteeFirstName.trim()) {
			errors.inviteeFirstName = "First name is required";
		}

		if (!formValues.inviteeLastName.trim()) {
			errors.inviteeLastName = "Last name is required";
		}

		if (!normalizedEmail) {
			errors.inviteeEmail = "Email is required";
		} else if (!emailRegex.test(normalizedEmail)) {
			errors.inviteeEmail = "Enter a valid email address";
		}

		if (
			formValues.inviteePhone.trim() &&
			!phoneRegex.test(formValues.inviteePhone.trim())
		) {
			errors.inviteePhone = "Enter a valid phone number";
		}

		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleChange =
		(field: keyof FormValues) =>
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;

			setFormValues((prev) => ({
				...prev,
				[field]: value,
			}));

			if (formErrors[field]) {
				setFormErrors((prev) => ({
					...prev,
					[field]: "",
				}));
			}

			if (submitError) setSubmitError("");
			if (submitSuccess) setSubmitSuccess("");
		};

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (disableSendInvite) {
			setSubmitError(
				inviteWindowMessage ||
					"Invites can only be sent during the allowed window period",
			);
			return;
		}

		if (!validateForm()) return;

		if (!user?._id) {
			setSubmitError("Unable to identify the current user.");
			return;
		}

		if (!circleId) {
			setSubmitError("Circle ID is required.");
			return;
		}

		setIsSubmitting(true);
		setSubmitError("");
		setSubmitSuccess("");

		const payload = {
			circleId,
			inviterUserId: user._id,
			inviteeFirstName: formValues.inviteeFirstName.trim(),
			inviteeLastName: formValues.inviteeLastName.trim(),
			inviteeEmail: normalizedEmail,
			inviteePhone: formValues.inviteePhone.trim() || undefined,
		};

		try {
			const response = await dispatch(sendCircleInvite(payload)).unwrap();

			if (response?.invitation?._id) {
				const inviteSentMsg =
					"Invitation sent successfully. The invitee will receive an email with next steps.";
				setSubmitSuccess(inviteSentMsg);
				setTimeout(() => {
					setSubmitSuccess("");
				}, 10000);
				// setNotificationMessage(inviteSentMsg);
				// setShowNotificationModal(true);
			}

			setFormValues(initialValues);
			setFormErrors({});
		} catch (error: any) {
			setSubmitError(
				typeof error === "string" ? error : (
					error?.message || "Failed to send invite. Please try again."
				),
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	// const handleCloseNotificationModal = () => {
	// 	setShowNotificationModal(false);
	// };

	return (
		<Dialog
			open={open}
			onClose={onCloseModal}
			fullScreen={fullScreen}
			fullWidth
			maxWidth="sm"
			PaperProps={{
				sx: {
					borderRadius: { xs: 0, sm: "20px" },
					overflow: "hidden",
					background:
						"linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
					boxShadow: "0 24px 80px rgba(15, 23, 42, 0.18)",
				},
			}}>
			<DialogTitle sx={{ p: 0 }}>
				<Box
					sx={{
						px: { xs: 2, sm: 3 },
						py: { xs: 2, sm: 2.5 },
						background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, ${buttonColors.mainSecondary} 100%)`,
						color: "#fff",
						position: "relative",
						overflow: "hidden",
					}}>
					<Box
						sx={{
							position: "absolute",
							width: 180,
							height: 180,
							borderRadius: "50%",
							background: "rgba(255,255,255,0.12)",
							top: -70,
							right: -50,
						}}
					/>
					<Box
						sx={{
							display: "flex",
							alignItems: "flex-start",
							justifyContent: "space-between",
							gap: 2,
							position: "relative",
							zIndex: 1,
						}}>
						<Stack direction="row" spacing={1.5} alignItems="center">
							<Box
								sx={{
									width: 46,
									height: 46,
									borderRadius: "14px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									backgroundColor: "rgba(255,255,255,0.18)",
								}}>
								<Groups2RoundedIcon />
							</Box>

							<Box>
								<Typography
									variant="h5"
									sx={{
										fontWeight: 700,
										fontSize: { xs: "1.1rem", sm: "1.35rem" },
									}}>
									Send Circle Invite
								</Typography>
								<Typography
									variant="body2"
									sx={{
										opacity: 0.92,
										mt: 0.5,
									}}>
									Invite someone to join {circleName || "your circle"}.
								</Typography>
							</Box>
						</Stack>

						<IconButton
							onClick={onCloseModal}
							disabled={isSubmitting}
							sx={{
								color: "#fff",
								backgroundColor: "rgba(255,255,255,0.12)",
								"&:hover": {
									backgroundColor: "rgba(255,255,255,0.2)",
								},
							}}>
							<CancelRoundedIcon />
						</IconButton>
					</Box>
				</Box>
			</DialogTitle>

			<DialogContent
				sx={{
					p: { xs: 2, sm: 3 },
				}}>
				<Card
					elevation={0}
					sx={{
						borderRadius: "18px",
						border: "1px solid rgba(148, 163, 184, 0.18)",
						background: "rgba(255,255,255,0.92)",
						boxShadow: "0 10px 40px rgba(15, 23, 42, 0.06)",
					}}>
					<CardContent sx={{ p: { xs: 2, sm: 3 } }}>
						<Stack spacing={2.5}>
							<Box>
								<Typography
									variant="h6"
									sx={{ fontWeight: 700, color: colors.darkGrey }}>
									Invite details
								</Typography>
								<Typography
									variant="body2"
									sx={{ mt: 0.5, color: "text.secondary" }}>
									Fill in the details below and we’ll send an invitation email.
								</Typography>
							</Box>

							<Divider />

							{submitError && <Alert severity="error">{submitError}</Alert>}
							{submitSuccess && (
								<Alert severity="success">{submitSuccess}</Alert>
							)}

							<Box component="form" onSubmit={handleSubmit}>
								<Grid container spacing={2}>
									<Grid item xs={12} sm={6}>
										<TextField
											fullWidth
											label="First name"
											value={formValues.inviteeFirstName}
											onChange={handleChange("inviteeFirstName")}
											error={!!formErrors.inviteeFirstName}
											helperText={formErrors.inviteeFirstName}
											disabled={isSubmitting}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<PersonOutlineRoundedIcon
															sx={{ color: "text.secondary", fontSize: 20 }}
														/>
													</InputAdornment>
												),
											}}
										/>
									</Grid>

									<Grid item xs={12} sm={6}>
										<TextField
											fullWidth
											label="Last name"
											value={formValues.inviteeLastName}
											onChange={handleChange("inviteeLastName")}
											error={!!formErrors.inviteeLastName}
											helperText={formErrors.inviteeLastName}
											disabled={isSubmitting}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<PersonOutlineRoundedIcon
															sx={{ color: "text.secondary", fontSize: 20 }}
														/>
													</InputAdornment>
												),
											}}
										/>
									</Grid>

									<Grid item xs={12}>
										<TextField
											fullWidth
											label="Email address"
											type="email"
											value={formValues.inviteeEmail}
											onChange={handleChange("inviteeEmail")}
											error={!!formErrors.inviteeEmail}
											helperText={
												formErrors.inviteeEmail ||
												"Invitation emails will be sent to this address."
											}
											disabled={isSubmitting}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<MailOutlineRoundedIcon
															sx={{ color: "text.secondary", fontSize: 20 }}
														/>
													</InputAdornment>
												),
											}}
										/>
									</Grid>

									{/* <Grid item xs={12}>
										<TextField
											fullWidth
											label="Phone number (optional)"
											value={formValues.inviteePhone}
											onChange={handleChange("inviteePhone")}
											error={!!formErrors.inviteePhone}
											helperText={
												formErrors.inviteePhone ||
												"Optional. Include country code when possible."
											}
											disabled={isSubmitting}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<PhoneIphoneRoundedIcon
															sx={{ color: "text.secondary", fontSize: 20 }}
														/>
													</InputAdornment>
												),
											}}
										/>
									</Grid> */}
								</Grid>

								<Stack
									direction={{ xs: "column-reverse", sm: "row" }}
									spacing={1.5}
									justifyContent="flex-end"
									sx={{ mt: 3 }}>
									<Button
										variant="outlined"
										onClick={onCloseModal}
										disabled={isSubmitting}
										sx={{
											minWidth: { xs: "100%", sm: 120 },
											height: 46,
											borderRadius: "12px",
											textTransform: "none",
											color: colors.darkGrey,
											borderColor: "rgba(100,116,139,0.28)",
										}}>
										Cancel
									</Button>

									<Tooltip
										title={
											disableSendInvite ? inviteWindowMessage : ""
										}
										arrow
										placement="top">
										<span
											style={{
												width: "100%",
												display: "inline-flex",
											}}>
											<Button
												type="submit"
												variant="contained"
												disabled={isSubmitting || disableSendInvite}
												startIcon={<SendRoundedIcon />}
												sx={{
													minWidth: { xs: "100%", sm: 170 },
													height: 46,
													borderRadius: "12px",
													textTransform: "none",
													fontWeight: 700,
													boxShadow: "none",
													background: buttonColors.mainSecondary,
													"&:hover": {
														boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
														background: buttonColors.mainSecondary,
													},
												}}>
												{isSubmitting ? "Sending..." : "Send Invite"}
											</Button>
										</span>
									</Tooltip>
								</Stack>
							</Box>
						</Stack>
					</CardContent>
					{/* {showNotificationModal && (
						<NotificationModal
							open={showNotificationModal}
							title="Invitation Sent"
							handleClose={handleCloseNotificationModal}
							sx={{
								width: "auto",
								maxWidth: "100%",
								margin: "0",
							}}>
							<Grid
								container
								spacing={2}
								direction="row"
								alignItems="center"
								justifyContent="center"
								sx={{
									margin: "0",
									width: "100%",
									padding: "0",
								}}>
								<Grid item>
									<Avatar
										src="/assets/icons/congrats-icon.gif"
										sx={{ width: 30, height: 30 }}
										alt="Congrats Icon"
									/>
								</Grid>
								<Grid item xs={9}>
									<Typography
										variant="body2"
										sx={{
											fontSize: 20,
											color: "text.secondary",
											overflow: "hidden",
											overflowWrap: "break-word",
											maxWidth: "100%",
										}}>
										{notificationMessage}
									</Typography>
								</Grid>
							</Grid>
						</NotificationModal>
					)} */}
				</Card>
			</DialogContent>
		</Dialog>
	);
};

export default SendCircleInviteModal;
